import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, XCircle, ShieldCheck, Printer, Calendar, Globe, FileSearch } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const CertificatePage = () => {
  const { certificateId } = useParams();
  const [cert, setCert] = useState(null);
  const [state, setState] = useState('loading');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/vendor/security/certificate/${certificateId}`);
        setCert(r.data);
        setState('ok');
      } catch {
        setState('error');
      }
    })();
  }, [certificateId]);

  const issuedDate = cert?.issued_at ? new Date(cert.issued_at) : null;

  return (
    <div className="min-h-screen bg-[#050f17] print:bg-white" data-testid="certificate-page">
      <div className="print:hidden">
        <Header />
      </div>

      <section className="mx-auto max-w-2xl px-4 pb-24 pt-32 print:pt-4 print:pb-4">
        {state === 'loading' && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          </div>
        )}

        {state === 'error' && (
          <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-10 text-center" data-testid="certificate-error">
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="mt-4 text-2xl font-bold text-white">Certificate not found</h1>
            <p className="mt-2 text-sm text-slate-400">This certificate ID doesn't match any completed Joomla 911 scan.</p>
            <Link to="/" className="mt-6 inline-block rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white">
              Back to Home
            </Link>
          </div>
        )}

        {state === 'ok' && (
          <div data-testid="certificate-render">
            <div className="mb-5 flex items-center justify-between print:hidden">
              <p className="text-sm text-slate-400">Certificate of Clean Health</p>
              <button
                type="button"
                onClick={() => window.print()}
                className="flex items-center gap-1.5 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-sm font-medium text-emerald-300 hover:bg-emerald-500/20 transition-colors"
                data-testid="print-certificate-btn"
              >
                <Printer className="h-4 w-4" /> Print Certificate
              </button>
            </div>

            <div className="relative overflow-hidden rounded-2xl border-4 border-emerald-500/30 bg-gradient-to-b from-[#0b1f16] to-[#050f17] p-10 shadow-2xl print:border-emerald-600 print:bg-white print:text-black print:shadow-none">
              <div
                className="pointer-events-none absolute inset-3 rounded-xl border border-dashed border-emerald-500/20 print:border-emerald-300"
                aria-hidden="true"
              />

              <div className="relative flex flex-col items-center text-center">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-emerald-500/15 print:bg-emerald-50">
                  <ShieldCheck className="h-11 w-11 text-emerald-400 print:text-emerald-600" />
                </div>

                <p className="mt-5 text-xs font-bold uppercase tracking-[0.3em] text-emerald-400 print:text-emerald-600">
                  Joomla 911 · AI Malware Scanner
                </p>
                <h1 className="mt-2 text-3xl font-black uppercase text-white print:text-black" data-testid="certificate-title">
                  Certificate of Clean Health
                </h1>
                <p className="mt-3 max-w-md text-sm text-slate-300 print:text-slate-700">
                  This certifies that the Joomla installation below was scanned by AI Joomla Security's
                  Joomla 911 malware scanner and had every flagged file reviewed and resolved.
                </p>

                <div className="mt-7 flex items-center gap-2 text-lg font-semibold text-white print:text-black" data-testid="certificate-site-url">
                  <Globe className="h-5 w-5 text-emerald-400 print:text-emerald-600" />
                  {cert.site_url}
                </div>

                <div className="mt-8 grid w-full grid-cols-3 gap-3 border-t border-emerald-500/20 pt-6 text-sm print:border-emerald-200">
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Files Scanned</p>
                    <p className="mt-1 text-lg font-bold text-white print:text-black" data-testid="certificate-files-scanned">{cert.files_scanned}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Files Flagged</p>
                    <p className="mt-1 text-lg font-bold text-white print:text-black" data-testid="certificate-files-flagged">{cert.files_flagged}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Files Cleaned</p>
                    <p className="mt-1 text-lg font-bold text-emerald-400 print:text-emerald-600" data-testid="certificate-files-cleaned">{cert.files_cleaned}</p>
                  </div>
                </div>

                <div className="mt-8 flex w-full items-center justify-between border-t border-emerald-500/20 pt-5 text-xs text-slate-400 print:border-emerald-200 print:text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    Issued {issuedDate ? issuedDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '--'}
                  </span>
                  <span className="flex items-center gap-1.5 font-mono" data-testid="certificate-number">
                    <FileSearch className="h-3.5 w-3.5" />
                    {cert.certificate_number}
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-4 text-center text-xs text-slate-500 print:hidden">
              Verify this certificate anytime at aijoomla.org/certificate/{cert.id}
            </p>
          </div>
        )}
      </section>

      <div className="print:hidden">
        <Footer />
      </div>
    </div>
  );
};

export default CertificatePage;
